import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Search, User } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';

interface UserData {
  fullName: string;
  isRegistered: boolean;
  isSubscribed: boolean;
  profilePhoto?: string;
}

const Header = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [user, setUser] = useState<UserData | null>(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [menuOpen, setMenuOpen] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const loadUser = () => {
      const userData = localStorage.getItem('user');
      if (userData) {
        setUser(JSON.parse(userData));
      }
    };
    loadUser();
    window.addEventListener('storage', loadUser);
    return () => window.removeEventListener('storage', loadUser);
  }, [location.pathname]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('user');
    setUser(null);
  };

  const renderUserSection = () => {
    if (!user?.isRegistered) {
      return (
        <Link to="/login" className="text-white hover:text-gray-300 transition-colors flex items-center gap-2">
          <User size={20} />
          <span>Login</span>
        </Link>
      );
    } else {
      return (
        <div className="flex items-center gap-3">
          <span className="text-white hidden md:block whitespace-nowrap">{user.fullName}</span>
          <div className="relative">
            <Avatar className="h-8 w-8">
              <AvatarImage src={user.profilePhoto || "/placeholder.svg"} alt={user.fullName} />
              <AvatarFallback>{user.fullName?.charAt(0) || "U"}</AvatarFallback>
            </Avatar>
          </div>
          <button
            onClick={handleLogout}
            className="text-white hover:text-gray-300 transition-colors text-sm hidden md:block"
          >
            Sair
          </button>
        </div>
      );
    }
  };

  if (isMobile) {
    return (
      <header className="fixed w-full top-0 z-50 bg-rustico-brown shadow-md border-b border-white/10 px-4 py-3 flex items-center justify-between">
        <Link to="/">
          <img src="/logo.png" alt="RusticoPlay Logo" className="h-[50px]" />
        </Link>

        <button
          onClick={() => setMenuOpen(!menuOpen)}
          className="w-10 h-10 flex flex-col items-center justify-center space-y-1.5 p-2 bg-white/10 rounded hover:bg-white/20"
        >
          <span className="block w-6 h-[2px] bg-white"></span>
          <span className="block w-6 h-[2px] bg-white"></span>
          <span className="block w-6 h-[2px] bg-white"></span>
        </button>

        {menuOpen && (
          <div className="absolute top-[70px] left-0 w-full bg-rustico-brown p-4 flex flex-col gap-4 z-50">
            <Link to="/" onClick={() => setMenuOpen(false)} className="text-white">Início</Link>
            <Link to="/shows" onClick={() => setMenuOpen(false)} className="text-white">Shows</Link>
            <Link to="/filmes" onClick={() => setMenuOpen(false)} className="text-white">Filmes</Link>
            <Link to="/series" onClick={() => setMenuOpen(false)} className="text-white">Séries</Link>
            <Link to="/minha-lista" onClick={() => setMenuOpen(false)} className="text-white">Minha Lista</Link>
            <Link to="/resenhas" onClick={() => setMenuOpen(false)} className="text-white">Resenhas</Link>
            <Link to="/fas-clubes" onClick={() => setMenuOpen(false)} className="text-white">Fãs Clubes</Link>
            <Link to="/social" onClick={() => setMenuOpen(false)} className="text-white">Live Online</Link>
            <form onSubmit={handleSearchSubmit} className="flex items-center gap-2 mt-2">
              <input
                type="search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Buscar..."
                className="flex-1 py-2 px-4 bg-[#333] text-white rounded"
              />
              <button type="submit" className="text-white">
                <Search size={20} />
              </button>
            </form>
            <div className="flex items-center justify-between mt-4">
              {renderUserSection()}
              {!user?.isSubscribed && (
                <Link to="/signup" className="bg-white text-rustico-brown px-4 py-2 rounded hover:bg-white/90">Assinar</Link>
              )}
            </div>
          </div>
        )}
      </header>
    );
  }

  return (
    <header className="fixed w-full top-0 z-50 bg-rustico-brown shadow-md border-b border-white/10 flex justify-between items-center px-[20px] py-0 h-[80px]">
      <Link to="/" className="flex items-center">
        <img src="/logo.png" alt="RusticoPlay Logo" className="h-[80px]" />
      </Link>

      <div className="flex items-center flex-1 justify-between ml-[20px]">
        <nav className="flex-1">
          <ul className="flex gap-[20px] p-0 m-0">
            <li><Link to="/" className={navClass(location, '/')} >Início</Link></li>
            <li><Link to="/shows" className={navClass(location, '/shows')} >Shows</Link></li>
            <li><Link to="/filmes" className={navClass(location, '/filmes')} >Filmes</Link></li>
            <li><Link to="/series" className={navClass(location, '/series')} >Séries</Link></li>
            <li><Link to="/minha-lista" className={navClass(location, '/minha-lista')} >Minha Lista</Link></li>
            <li><Link to="/resenhas" className={navClass(location, '/resenhas')} >Resenhas</Link></li>
            <li><Link to="/fas-clubes" className={navClass(location, '/fas-clubes')} >Fãs Clubes</Link></li>
            <li><Link to="/social" className={navClass(location, '/social')} >Live Online</Link></li>
          </ul>
        </nav>

        <form onSubmit={handleSearchSubmit} className="flex items-center w-[260px] bg-[#333] rounded-full overflow-hidden shadow-inner">
          <input
            type="search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Buscar..."
            className="flex-1 py-2 px-4 bg-transparent text-white focus:outline-none"
          />
          <button
            type="submit"
            className="p-2 text-white hover:text-gray-300 focus:outline-none"
          >
            <Search size={20} />
          </button>
        </form>

        <div className="ml-6 flex items-center gap-4">
          {renderUserSection()}
          {!user?.isSubscribed && (
            <Link
              to="/signup"
              className="bg-white text-rustico-brown px-4 py-2 rounded-md hover:bg-white/90 transition-colors"
            >
              Assinar
            </Link>
          )}
        </div>
      </div>
    </header>
  );
};

function navClass(location: any, path: string) {
  return `${location.pathname === path ? 'text-white' : 'text-white/70'} hover:text-white transition-colors`;
}

export default Header;
